import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import type { ActionContract } from 'model/actions';
import { groupActionsByCategory } from 'model/actions';
import { useDragDrop } from 'hooks/useDragDrop';
import { useActionsCatalog } from 'hooks/useActionsCatalog';

const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStatusText = styled.p<{ $danger?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme, $danger }) => ($danger ? theme.colors.dangerText : theme.colors.textMuted)};
  font-weight: ${({ $danger }) => ($danger ? 400 : 300)};
`;

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledCategoryHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  margin-top: 0.375rem;
  cursor: pointer;
  user-select: none;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  border: none;
  text-align: left;
  width: 100%;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textSecondary};
    background: transparent;
  }
`;

const StyledCaret = styled.span<{ $collapsed: boolean }>`
  display: inline-block;
  font-size: 9px;
  transition: transform 0.15s;
  transform: ${({ $collapsed }) => ($collapsed ? 'rotate(-90deg)' : 'rotate(0deg)')};
`;

const StyledCategoryCount = styled.span`
  font-weight: 400;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const StyledItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-left: 0.25rem;
`;

const StyledActionTile = styled.div`
  padding: 0.375rem 0.625rem;
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.panel};
  cursor: grab;
  user-select: none;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition:
    box-shadow 0.15s,
    border-color 0.15s;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.paletteHover};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ActionPalette = () => {
  const { actions, isLoading, error } = useActionsCatalog();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const { handleDragStart } = useDragDrop<ActionContract>();

  const grouped = useMemo(() => groupActionsByCategory(actions), [actions]);

  const handleToggleGroup = useCallback((category: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }

      return next;
    });
  }, []);

  return (
    <StyledRoot>
      <h4>Actions</h4>
      {isLoading && <StyledStatusText>Loading...</StyledStatusText>}
      {error && <StyledStatusText $danger>Error: {error}</StyledStatusText>}
      {!isLoading && !error && actions.length === 0 && (
        <StyledStatusText>No actions available.</StyledStatusText>
      )}
      <StyledList>
        {Array.from(grouped.entries()).map(([category, groupActions]) => {
          const isCollapsed = collapsedGroups.has(category);
          return (
            <div key={category}>
              <StyledCategoryHeader type='button' onClick={() => handleToggleGroup(category)}>
                <StyledCaret $collapsed={isCollapsed}>&#9660;</StyledCaret>
                {category}
                <StyledCategoryCount>({groupActions.length})</StyledCategoryCount>
              </StyledCategoryHeader>
              {!isCollapsed && (
                <StyledItems>
                  {groupActions.map((action) => (
                    <StyledActionTile
                      key={action.actionId}
                      draggable
                      onDragStart={handleDragStart(action)}
                    >
                      {action.displayName}
                    </StyledActionTile>
                  ))}
                </StyledItems>
              )}
            </div>
          );
        })}
      </StyledList>
    </StyledRoot>
  );
};

const MemoActionPalette = React.memo(ActionPalette);

export { MemoActionPalette as ActionPalette };
export default MemoActionPalette;
